import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon, HomeIcon, MapIcon } from 'lucide-react';
import { disclaimer } from '../data/teaser';
import { brandLogo } from '../data/journey';
import type { BrandLevel } from '../data/journey';
interface PhoneFrameProps {
  step: number;
  total: number;
  children: React.ReactNode;
  footnote?: string;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  /** Opens the internal strategy document. */
  onOpenStrategy: () => void;
  /** Opens the campaign journey visualisation. */
  onOpenJourney: () => void;
  /** Chapter label shown beside the screen counter, e.g. "Snak 2". */
  chapter?: string;
  railEyebrow?: string;
  /** Two-line rail headline. */
  railTitle?: string[];
  railBlurb?: string;
  railDisclaimer?: string;
  /** How much brand identity this phase may carry. */
  brand?: BrandLevel;
  /** Returns to the entry menu. Hidden on the menu itself. */
  onHome?: () => void;
  /** Label for the current mode, shown under the rail counter. */
  modeLabel?: string;
}
export function PhoneFrame({
  step,
  total,
  children,
  footnote,
  onBack,
  onNext,
  nextLabel = 'Continue',
  nextDisabled = false,
  onOpenStrategy,
  onOpenJourney,
  chapter,
  railEyebrow = 'Unbranded disease awareness · medical retina',
  railTitle = ['See beyond', 'the fluid.'],
  railBlurb = 'A 60-second teaser for Danish retina specialists on DME phenotypes and AI-assisted OCT biomarker analysis.',
  railDisclaimer = disclaimer,
  brand = 'none',
  onHome,
  modeLabel
}: PhoneFrameProps) {
  const isDark = brand === 'full';
  return <div className="flex min-h-full w-full items-center justify-center bg-mist px-5 py-8" data-id="element-112" data-mp="3iufr2lvs7q20">
      <div className="flex w-full max-w-5xl items-center justify-center gap-16" data-id="element-113" data-mp="3dwdz1bgdogee">
        <aside className="hidden max-w-xs flex-1 lg:block" data-id="element-114" data-mp="27fsh44j3tnlk">
          <p className="text-[10px] uppercase tracking-micro text-mut" data-id="element-115" data-mp="27hdb9t1ib4iw">{railEyebrow}</p>
          <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] text-navy" data-id="element-116" data-mp="22fwf2atyw68n">
            {railTitle[0]}
            <br data-id="element-117" data-mp="23ynxgyxxe1dv" />
            {railTitle[1]}
          </h1>
          <p className="mt-5 text-sm leading-relaxed text-mut" data-id="element-118" data-mp="28x7o3cngs1ck">{railBlurb}</p>
          {total > 0 && <p className="mt-6 text-[11px] uppercase tracking-micro text-mut" data-id="element-119" data-mp="1uw4zot8zyeyh">
              {chapter ? `${chapter} · ` : ''}Screen {step + 1} of {total}
            </p>}
          {modeLabel && <p className="mt-1.5 text-[11px] uppercase tracking-micro text-mut" data-id="element-120" data-mp="1l6lf9j5myd1i">{modeLabel}</p>}
          <p className="mt-6 border-t border-line pt-4 text-[11px] leading-relaxed text-mut" data-id="element-121" data-mp="1pzob5xxvxykv">
            {railDisclaimer}
          </p>
        </aside>

        <div className="flex w-full max-w-[400px] flex-col" data-id="element-122" data-mp="1g80oeac9x5pq">
          <div className={`flex aspect-[9/16] w-full flex-col overflow-hidden rounded-[34px] border shadow-soft ${isDark ? 'border-ozNavy bg-ozNavy' : brand === 'colour' ? 'border-line bg-ozMist/50' : 'border-line bg-paper'}`} data-id="element-123" data-mp="1g8jw6y87mdq1">
            {brand !== 'none' && <div className={`h-1 w-full shrink-0 ${isDark ? 'bg-ozGreen' : 'bg-ozBlue'}`} aria-hidden="true" data-id="element-124" data-mp="ioa8lrj0p603" />}

            <div className="flex items-center justify-between gap-3 px-6 pt-5" data-id="element-125" data-mp="sknavdsmos6g">
              <div className="flex min-w-0 items-center gap-2" data-id="element-126" data-mp="16kejt04afidl">
                {onHome && <button type="button" onClick={onHome} aria-label="Back to the entry menu" className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors duration-150 focus:outline-none focus-visible:ring-2 ${isDark ? 'border-paper/25 text-paper/70 hover:border-ozGreen hover:text-paper focus-visible:ring-ozGreen' : 'border-line text-mut hover:border-mut hover:text-navy focus-visible:ring-blue'}`} data-id="element-127" data-mp="11luwowdyk0jn">
                    <HomeIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-128" data-mp="3jqcny8uyu12g" />
                  </button>}
                {isDark ? <span className="rounded bg-paper px-2 py-1" data-id="element-129" data-mp="3okgwgwjvqh5n">
                    <img src={brandLogo.src} alt={brandLogo.alt} draggable={false} className="h-5 w-auto select-none object-contain object-left" data-id="element-130" data-mp="3sj6h9a78z0zb" />
                  </span> : chapter && <span className="truncate text-[9px] uppercase tracking-micro text-mut" data-id="element-131" data-mp="34wgci13iksb0">
                      {chapter}
                    </span>}
              </div>

              <div className="flex shrink-0 items-center gap-1.5" data-id="element-132" data-mp="p2zauvxg6pmu">
                <button type="button" onClick={onOpenJourney} aria-label="See the campaign journey" className={`flex h-7 w-7 items-center justify-center rounded-full border transition-colors duration-150 focus:outline-none focus-visible:ring-2 ${isDark ? 'border-ozGreen/60 text-ozGreen hover:bg-ozGreen/15 focus-visible:ring-ozGreen' : 'border-blue/60 bg-paper text-navy hover:border-blue hover:bg-sky/50 focus-visible:ring-blue'}`} data-id="element-133" data-mp="p14rvn5ql7le">
                  <MapIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-134" data-mp="bjeoey4cdmjo" />
                </button>
                <button type="button" onClick={onOpenStrategy} className={`rounded-full border px-3 py-1 text-[10.5px] font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 ${isDark ? 'border-ozGreen/60 text-paper hover:bg-ozGreen/15 focus-visible:ring-ozGreen' : 'border-blue/60 bg-paper text-navy hover:border-blue hover:bg-sky/50 focus-visible:ring-blue'}`} data-id="element-135" data-mp="6kbhf84sx692">
                  Strategy
                </button>
              </div>
            </div>

            <div className="flex flex-1 flex-col overflow-y-auto px-6 pb-5 pt-3" data-id="element-136" data-mp="3le1029940a3p">{children}</div>

            {(onNext || onBack || footnote) && <div className={`border-t px-6 py-4 ${isDark ? 'border-paper/12' : 'border-line'}`} data-id="element-137" data-mp="cz0nknmcra13">
                {footnote && <p className={`mb-3 text-[9px] leading-relaxed ${isDark ? 'text-paper/45' : 'text-mut'}`} data-id="element-138" data-mp="3xszszhgprl9">
                    {footnote}
                  </p>}
                {(onNext || onBack) && <div className="flex items-center gap-2" data-id="element-139" data-mp="2tn4m15bbbzeu">
                    {onBack && <button type="button" onClick={onBack} aria-label="Previous screen" className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-colors duration-150 focus:outline-none focus-visible:ring-2 ${isDark ? 'border-paper/25 text-paper/70 hover:border-ozGreen hover:text-paper focus-visible:ring-ozGreen' : 'border-line text-mut hover:border-mut hover:text-navy focus-visible:ring-blue'}`} data-id="element-140" data-mp="2l641i9cqh1vt">
                        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-141" data-mp="2c6qsfhsiufbm" />
                      </button>}
                    {onNext && <button type="button" onClick={onNext} disabled={nextDisabled} className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-full px-5 text-[13px] font-semibold transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-30 ${isDark ? 'bg-ozGreen text-ozNavy focus-visible:ring-ozGreen focus-visible:ring-offset-ozNavy' : 'bg-navy text-paper focus-visible:ring-blue focus-visible:ring-offset-2'}`} data-id="element-142" data-mp="1jo3a2a4wtee8">
                        {nextLabel}
                        <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-143" data-mp="1lvo1aiv6w0gr" />
                      </button>}
                  </div>}
              </div>}
          </div>
        </div>
      </div>
    </div>;
}